/**
 * The external picture: what other people's systems reported, and whether the
 * feeds that carried it are still alive.
 *
 * A store rather than a `useState` in a screen, for the reason supplyStore
 * gives: the map layer draws contacts without React, and a hook that only
 * exists while one screen is mounted cannot feed a layer on Command *and*
 * Planning. `subscribeToExternalPicture` is that seam.
 *
 * ── The lifecycle, which is the whole point of this file ────────────────────
 *
 * `docs/INTEGRATION-LAYER.md` §5 names four facts that would otherwise render
 * identically, and getting them to render identically is the failure that
 * section exists to prevent:
 *
 *   NO_LONGER_REPORTED  the source stopped mentioning it, and we do not know
 *                       why. Reads "last heard 4m ago from ADSB-1".
 *   RETIRED_BY_SOURCE   the source explicitly said it is gone. Reads as a
 *                       removal WITH a reason that names the source.
 *   SOURCE_DOWN         the whole feed died. Every contact from it reads
 *                       "source offline" — never two hundred contacts
 *                       independently going stale, which an operator reads as
 *                       a mass event in the world rather than a broken cable.
 *   SUPERSEDED          still reported, but somebody else carries it now. The
 *                       contact stays; the provenance changes.
 *
 * Three of those are not on the wire, and this is where they are recovered:
 *
 * The server does not flag a retirement — `live_observations` drops a retired
 * contact out of the result entirely (`AND retired = 0`), which is right for
 * the picture and leaves the *reason* only inferable from the departure. So
 * departures are watched. A contact that leaves the live list **while it was
 * still fresh** can only have left because the source said it was gone; one
 * that leaves **after it had already gone stale** simply lapsed out of the
 * window. That single distinction is what separates the first two rows above,
 * and it is why `previous` is kept across polls rather than each poll being
 * treated as a fresh world.
 *
 * SOURCE_DOWN outranks everything. It is asserted from the feed's own health
 * record — a fact with its own timestamp, exactly as `supply_history` separates
 * a heartbeat from a change so a flat line is distinguishable from a recorder
 * that was down. A cut feed counts too: when an operator disables a source the
 * server drops its contacts *immediately and while fresh*, which would
 * otherwise read as two hundred simultaneous retirements.
 *
 * SUPERSEDED is reachable in phase 1 without any correlator. Track ids are
 * `EXT.{source}.{their uid}`, so the same uid arriving through two feeds is two
 * tracks. When one of those feeds is cut or dies, its contacts depart while a
 * live contact from another feed still carries the same `sourceTrackId` — the
 * object did not go anywhere, the feed carrying it changed. Calling that a
 * retirement would be a lie about the world; calling it superseded names what
 * actually happened and who has it now.
 *
 * Departed contacts are held for a bounded grace period and then dropped. Long
 * enough that the operator sees *why* something left, short enough that the map
 * never accumulates ghosts — a contact that outlives its truth is worse than a
 * gap somebody can see.
 */

import { create } from 'zustand';

import {
  getExternalSources,
  getExternalTracks,
  setExternalSourceEnabled,
  type ExternalSource,
  type ExternalTrack,
  type TrackContradiction,
} from '@/api/external';

/**
 * How long a departed contact stays on the map carrying its reason.
 *
 * Two minutes: a removal an operator did not witness is a removal they will
 * plan around wrongly, and 120 s is roughly one glance-away. Deliberately far
 * shorter than the server's 600 s live window, so a ghost can never outlive the
 * history that explains it.
 */
export const DEPARTED_GRACE_S = 120;

export type TrackLifecycle =
  | 'live'
  | 'no-longer-reported'
  | 'retired-by-source'
  | 'source-down'
  | 'superseded';

/** One shared empty array per collection, so "nothing loaded" has a stable identity. */
const NO_TRACKS: ExternalTrack[] = [];
const NO_SOURCES: ExternalSource[] = [];
const NO_CONTACTS: ExternalContact[] = [];
// Shared empty arrays: a fresh [] every poll re-renders every subscriber.
const NO_CONTRADICTIONS: TrackContradiction[] = [];
const NO_MERGES: MergeGroup[] = [];

/**
 * A contact as the map draws it: what the source said, plus what became of it.
 *
 * `track` is verbatim from exactly one named source and is never merged,
 * averaged or adjusted here. Everything this store adds is about the *fate* of
 * that statement, and is kept in separate fields so the two can never be
 * confused for each other.
 */
export interface ExternalContact {
  track: ExternalTrack;
  lifecycle: TrackLifecycle;
  /** False once the contact has left the live list. Still drawn, differently. */
  present: boolean;
  /** Unix seconds we last saw this contact in a live response. */
  lastSeenAt: number;
  /**
   * The source that took it over, for `superseded`. null otherwise. Named
   * rather than implied: "somebody else has it" is not an answer.
   */
  supersededBy: string | null;
  /**
   * Why the feed is not delivering, for `source-down`. Distinguishes a link
   * that failed from one a human deliberately cut — same lifecycle, opposite
   * things to do about it.
   */
  sourceDownReason: 'offline' | 'cut-by-operator' | null;
}

interface Departed {
  track: ExternalTrack;
  lastSeenAt: number;
  /** Whether the contact was still inside its freshness when it vanished. */
  wasFresh: boolean;
}

/**
 * Where each source stood at the moment a poll was classified.
 *
 * A record rather than the `ExternalSource[]` itself so the classifier stays a
 * pure function of two small values and can be tested without a store.
 */
export interface SourceState {
  /** True when the feed's own health record says `live`. */
  up: boolean;
  /** False when an operator has cut it. */
  enabled: boolean;
}

export function sourceStates(sources: ExternalSource[]): Record<string, SourceState> {
  const states: Record<string, SourceState> = {};
  for (const source of sources) {
    states[source.sourceId] = {
      // No health record yet is not "up". A feed that has never reported is
      // exactly the one an operator must not assume is delivering.
      up: source.health !== null && source.health.state === 'live',
      enabled: source.enabled,
    };
  }
  return states;
}

/**
 * What became of every contact we know about, live and departed.
 *
 * Pure, and the precedence is the argument:
 *
 *   1. The feed is down or cut → `source-down`, whatever the contact's own age
 *      says. This has to outrank staleness or a dead cable renders as a mass
 *      event in the world.
 *   2. Departed, and another live feed carries the same uid → `superseded`.
 *   3. Departed while still fresh → `retired-by-source`. The only way out of
 *      the live list while fresh is the source saying it is gone.
 *   4. Departed after going stale → `no-longer-reported`. It lapsed.
 *   5. Present and stale → `no-longer-reported`. It is lapsing now.
 *   6. Present and fresh → `live`.
 */
export function classify(
  live: ExternalTrack[],
  departed: Departed[],
  states: Record<string, SourceState>,
  now: number,
): ExternalContact[] {
  // Which uids are currently carried by a live feed, and by whom. Built once:
  // the supersession test is asked per departed contact and a scan each time
  // would be quadratic on a feed that just died with two thousand contacts.
  const carriers = new Map<string, string>();
  for (const track of live) {
    carriers.set(track.source.sourceTrackId, track.source.sourceId);
  }

  const down = (track: ExternalTrack): ExternalContact['sourceDownReason'] => {
    const state = states[track.source.sourceId];
    if (state !== undefined && !state.enabled) return 'cut-by-operator';
    // The per-track flag is the server's own read of the same health record.
    // Trusted first so a contact from a source that vanished from the source
    // list entirely still reports the truth.
    if (track.source.offline) return 'offline';
    if (state !== undefined && !state.up) return 'offline';
    return null;
  };

  const contacts: ExternalContact[] = live.map((track) => {
    const reason = down(track);
    if (reason !== null) {
      return {
        track,
        lifecycle: 'source-down',
        present: true,
        lastSeenAt: now,
        supersededBy: null,
        sourceDownReason: reason,
      };
    }
    return {
      track,
      lifecycle: track.stale ? 'no-longer-reported' : 'live',
      present: true,
      lastSeenAt: now,
      supersededBy: null,
      sourceDownReason: null,
    };
  });

  for (const entry of departed) {
    const { track } = entry;
    const reason = down(track);
    if (reason !== null) {
      contacts.push({
        track,
        lifecycle: 'source-down',
        present: false,
        lastSeenAt: entry.lastSeenAt,
        supersededBy: null,
        sourceDownReason: reason,
      });
      continue;
    }

    const carrier = carriers.get(track.source.sourceTrackId);
    if (carrier !== undefined && carrier !== track.source.sourceId) {
      contacts.push({
        track,
        lifecycle: 'superseded',
        present: false,
        lastSeenAt: entry.lastSeenAt,
        supersededBy: carrier,
        sourceDownReason: null,
      });
      continue;
    }

    contacts.push({
      track,
      lifecycle: entry.wasFresh ? 'retired-by-source' : 'no-longer-reported',
      present: false,
      lastSeenAt: entry.lastSeenAt,
      supersededBy: null,
      sourceDownReason: null,
    });
  }

  return contacts;
}

/**
 * The departed list after one poll: whoever left, minus whoever came back,
 * minus whoever has been gone longer than the grace period.
 */
export function nextDeparted(
  previousLive: ExternalTrack[],
  previousDeparted: Departed[],
  live: ExternalTrack[],
  now: number,
): Departed[] {
  const stillHere = new Set(live.map((track) => track.trackId));
  const departed: Departed[] = [];
  const seen = new Set<string>();

  for (const track of previousLive) {
    if (stillHere.has(track.trackId)) continue;
    seen.add(track.trackId);
    // Freshness as of the poll it was last present in. Recomputing it against
    // `now` would turn every retirement into a lapse a few seconds later, which
    // is precisely the collapse §5 is about.
    departed.push({ track, lastSeenAt: now, wasFresh: !track.stale });
  }

  for (const entry of previousDeparted) {
    if (stillHere.has(entry.track.trackId)) continue;
    if (seen.has(entry.track.trackId)) continue;
    if (now - entry.lastSeenAt > DEPARTED_GRACE_S) continue;
    departed.push(entry);
  }

  return departed;
}

interface ExternalStoreState {
  /** Verbatim from the last successful poll. Never edited. */
  tracks: ExternalTrack[];
  departed: Departed[];
  /** The classified picture the map draws. Recomputed on every poll. */
  contacts: ExternalContact[];
  sources: ExternalSource[];

  /** False when this server has no ingest configured at all. */
  configured: boolean;
  /** Null until the first successful poll. */
  generatedAt: number | null;
  loading: boolean;
  error: string | null;
  /** Set by the enable/disable switch, so the panel can say what refused. */
  switchBusy: string | null;
  /**
   * Feeds that coincide on a contact and disagree about what it is.
   *
   * Kept as its own list rather than folded into the contacts, because that is
   * what it is: the correlator refused to merge these and refused to pick a
   * winner, and a disagreement flattened into one marker is the finding
   * deleted. Empty is the normal state.
   */
  contradictions: TrackContradiction[];
  /** Recomputed on every poll from the rows, never fetched separately. */
  merges: MergeGroup[];

  loadTracks: () => Promise<void>;
  loadSources: () => Promise<void>;
  setSourceEnabled: (sourceId: string, enabled: boolean) => Promise<{ ok: boolean; message: string }>;
  reset: () => void;
}

export const useExternalStore = create<ExternalStoreState>((set, get) => ({
  tracks: NO_TRACKS,
  departed: [],
  contacts: NO_CONTACTS,
  sources: NO_SOURCES,

  configured: false,
  generatedAt: null,
  loading: false,
  error: null,
  switchBusy: null,
  contradictions: NO_CONTRADICTIONS,
  merges: NO_MERGES,

  loadTracks: async () => {
    set({ loading: true });
    const result = await getExternalTracks();
    if (!result.ok) {
      // The previous picture is left in place. Blanking every external contact
      // because one poll timed out is a mass disappearance caused by our own
      // network, which is the same lie §5 is about — pointed at ourselves.
      set({ loading: false, error: result.error.message });
      return;
    }
    const now = result.data.generatedAt;
    const state = get();
    const departed = nextDeparted(state.tracks, state.departed, result.data.tracks, now);
    set({
      loading: false,
      error: null,
      configured: result.data.configured,
      generatedAt: now,
      tracks: result.data.tracks,
      // A server without the field is not a server with no conflicts, so an
      // absent list stays empty rather than being read as an all-clear.
      contradictions: result.data.contradictions ?? NO_CONTRADICTIONS,
      merges: mergeGroups(result.data.tracks),
      departed,
      contacts: classify(result.data.tracks, departed, sourceStates(state.sources), now),
    });
  },

  loadSources: async () => {
    const result = await getExternalSources();
    if (!result.ok) {
      set({ error: result.error.message });
      return;
    }
    const state = get();
    set({
      sources: result.data.sources,
      configured: state.configured || result.data.configured,
      // Health moved, so what every contact means moved with it. Reclassifying
      // here is what makes a feed dropping read as "source offline" on the map
      // within one poll rather than only at the next track fetch.
      contacts: classify(
        state.tracks,
        state.departed,
        sourceStates(result.data.sources),
        state.generatedAt ?? Date.now() / 1000,
      ),
    });
  },

  setSourceEnabled: async (sourceId, enabled) => {
    set({ switchBusy: sourceId });
    const result = await setExternalSourceEnabled(sourceId, enabled);
    if (!result.ok) {
      set({ switchBusy: null, error: result.error.message });
      return { ok: false, message: result.error.message };
    }
    set({ switchBusy: null, error: null });
    // The switch changes what the picture contains, immediately and on purpose:
    // a cut feed's contacts leave the live list rather than ageing out of it.
    await get().loadSources();
    await get().loadTracks();
    return {
      ok: true,
      message: enabled ? `Feed ${sourceId} resumed.` : `Feed ${sourceId} cut.`,
    };
  },

  reset: () =>
    set({
      tracks: NO_TRACKS,
      departed: [],
      contacts: NO_CONTACTS,
      sources: NO_SOURCES,
      configured: false,
      generatedAt: null,
      loading: false,
      error: null,
      switchBusy: null,
    }),
}));

/**
 * Watch the external picture without React, the way the supply layer watches
 * sites. Fires only when the classified contacts change identity — a busy flag
 * or an error message must not redraw the map.
 */
export function subscribeToExternalPicture(
  callback: (contacts: ExternalContact[]) => void,
): () => void {
  let previous = useExternalStore.getState().contacts;
  callback(previous);

  return useExternalStore.subscribe((state) => {
    if (state.contacts === previous) return;
    previous = state.contacts;
    callback(state.contacts);
  });
}

/**
 * Actions at module scope, stable forever. A component that reaches one action
 * through `useExternalStore()` has subscribed to every field in this file.
 */
export const externalActions = {
  loadTracks: () => useExternalStore.getState().loadTracks(),
  loadSources: () => useExternalStore.getState().loadSources(),
  setSourceEnabled: (sourceId: string, enabled: boolean) =>
    useExternalStore.getState().setSourceEnabled(sourceId, enabled),
  reset: () => useExternalStore.getState().reset(),
};

/* Selectors. One question each, so a source toggle does not re-render the map
 * overlay and a new poll does not re-render the feed panel's switches. */

export function useExternalContacts(): ExternalContact[] {
  return useExternalStore((state) => state.contacts);
}

export function useExternalSources(): ExternalSource[] {
  return useExternalStore((state) => state.sources);
}

export function useExternalConfigured(): boolean {
  return useExternalStore((state) => state.configured);
}

/** One object the picture has assembled out of several reports. */
export interface MergeGroup {
  mergeId: string;
  /** The reports folded in, sorted, so the list does not reorder itself. */
  trackIds: string[];
  /** What each contributing feed calls it — often not the same thing. */
  callsigns: string[];
}

/**
 * The merges currently on the map, derived from the per-report rows.
 *
 * Derived rather than fetched: the rows already say which merged object each
 * belongs to, and a second endpoint would be a second answer to the same
 * question that could disagree with the first.
 */
export function mergeGroups(tracks: ExternalTrack[]): MergeGroup[] {
  const byMerge = new Map<string, ExternalTrack[]>();
  for (const track of tracks) {
    if (track.mergedInto === null) continue;
    const group = byMerge.get(track.mergedInto) ?? [];
    group.push(track);
    byMerge.set(track.mergedInto, group);
  }
  return [...byMerge.entries()]
    .map(([mergeId, group]) => ({
      mergeId,
      trackIds: group.map((t) => t.trackId).sort(),
      callsigns: [...new Set(group.map((t) => t.callsign).filter(Boolean))].sort(),
    }))
    .sort((a, b) => a.mergeId.localeCompare(b.mergeId));
}

/** The raw per-report rows, for anything that has to act on one of them. */
export function useExternalTracks(): ExternalTrack[] {
  return useExternalStore((state) => state.tracks);
}

export function useExternalMerges(): MergeGroup[] {
  return useExternalStore((state) => state.merges);
}

export function useExternalContradictions(): TrackContradiction[] {
  return useExternalStore((state) => state.contradictions);
}

export function useExternalError(): string | null {
  return useExternalStore((state) => state.error);
}

export function useExternalSwitchBusy(): string | null {
  return useExternalStore((state) => state.switchBusy);
}
